import React, { useState } from "react";
import "./awscommandbuilder.css";
import Page from "../../Utilities/Page";

const COMMANDS = [
  {
    label: "s3 ls",
    base: "aws s3 ls",
    options: [
      { name: "path", placeholder: "bucket-name/path/" },
      { name: "recursive", type: "flag" },
      { name: "no-sign-request", type: "flag" },
    ],
  },
  {
    label: "s3 cp",
    base: "aws s3 cp",
    options: [
      { name: "source", placeholder: "bucket/file.txt" },
      { name: "destination", placeholder: "-" },
      { name: "no-sign-request", type: "flag" },
    ],
  },
  {
    label: "s3api get-bucket-versioning",
    base: "aws s3api get-bucket-versioning",
    options: [{ name: "bucket", placeholder: "bucket-name" }, { name: "no-sign-request", type: "flag" }],
  },
  {
    label: "s3api list-object-versions",
    base: "aws s3api list-object-versions",
    options: [
      { name: "bucket", placeholder: "bucket-name" },
      { name: "query", placeholder: 'Versions[?VersionId!=\\\'null\\\']' },
      { name: "no-sign-request", type: "flag" },
    ],
  },
  {
    label: "s3api get-object",
    base: "aws s3api get-object",
    options: [
      { name: "bucket", placeholder: "bucket-name" },
      { name: "key", placeholder: "filename.txt" },
      { name: "version-id", placeholder: "SAMPLEqgWpDiIwY05TGdUvTnGJSH49frH_7.yhSAMPLE" },
      { name: "output-file", placeholder: "output_filename.txt", type: "positional" },
      { name: "no-sign-request", type: "flag" },
    ],
  },
  {
    label: "secretsmanager list-secrets",
    base: "aws secretsmanager list-secrets",
    options: [],
  },
  {
    label: "secretsmanager get-secret-value",
    base: "aws secretsmanager get-secret-value",
    options: [
      { name: "secret-id", placeholder: "arn:aws:secretsmanager:region:account-id:secret:name" },
    ],
  },
  {
    label: "lambda list-functions",
    base: "aws lambda list-functions",
    options: [],
  },
  {
    label: "lambda get-function",
    base: "aws lambda get-function",
    options: [
      { name: "function-name", placeholder: "lambda-name" },
      { name: "query", placeholder: "'Code.Location'" },
    ],
  },
  {
    label: "wget lambda zip",
    base: "wget -O lambda-function.zip",
    options: [
      { name: "url", placeholder: "http://..." },
    ],
  },
  {
    label: "lambda invoke",
    base: "aws lambda invoke",
    options: [
      { name: "cli-binary-format", placeholder: "raw-in-base64-out" },
      { name: "function-name", placeholder: "lambda-name" },
      { name: "payload", placeholder: '{ "key": "value" }' },
      { name: "output-file", placeholder: "response.json", type: "positional" },
    ],
  },
  {
    label: "sts get-caller-identity",
    base: "aws sts get-caller-identity",
    options: [],
  },
];

const AwsCommandBuilder = () => {
  const [selectedCommand, setSelectedCommand] = useState(COMMANDS[0]);
  const [optionValues, setOptionValues] = useState({});
  const [useLocalStack, setUseLocalStack] = useState(true);
  const [copyStatus, setCopyStatus] = useState("Copy to Clipboard");

  const buildCommand = () => {
    let cmd = selectedCommand.base;

    if (useLocalStack && cmd.startsWith("aws")) {
      cmd = `aws --endpoint-url=http://localhost:4566 ${cmd.substring(4)}`;
    }

    // Special handling for s3 cp
    if (selectedCommand.label === "s3 cp") {
      let source = optionValues["source"] || "s3://" + (COMMANDS.find(c => c.label === "s3 cp").options.find(o => o.name === "source").placeholder);
      let destination = optionValues["destination"] || COMMANDS.find(c => c.label === "s3 cp").options.find(o => o.name === "destination").placeholder;

      if (!source.startsWith("s3://")) {
        source = `s3://${source}`;
      }

      cmd += ` ${source} ${destination}`;

      // add any flags
      selectedCommand.options.forEach((opt) => {
        if (opt.type === "flag") {
          const val = optionValues[opt.name];
          if (val) {
            cmd += ` --${opt.name}`;
          }
        }
      });

      return cmd;
    }

    if (selectedCommand.label === "lambda invoke") {
      selectedCommand.options.forEach((opt) => {
        let val = optionValues[opt.name] || opt.placeholder || "";

        if (opt.name === "payload") {
          // auto-wrap payload in single quotes
          if (val && !val.startsWith("'")) {
            val = `'${val}'`;
          }
          cmd += ` --${opt.name} ${val}`;
        } else if (opt.type === "flag") {
          if (optionValues[opt.name]) {
            cmd += ` --${opt.name}`;
          }
        } else if (opt.type === "positional") {
          if (val) {
            cmd += ` ${val}`;
          }
        } else if (val) {
          cmd += ` --${opt.name} ${val}`;
        }
      });

      return cmd;
    }

    // Special handling for s3 ls
    if (selectedCommand.label === "s3 ls") {
      selectedCommand.options.forEach((opt) => {
        let val = optionValues[opt.name] || opt.placeholder || "";

        if (opt.name === "path") {
          if (val && !val.startsWith("s3://")) {
            val = `s3://${val}`;
          }
          if (val) {
            cmd += ` ${val}`;
          }
          return;
        }

        if (opt.type === "flag") {
          if (optionValues[opt.name]) {
            cmd += ` --${opt.name}`;
          }
        } else if (val) {
          cmd += ` --${opt.name} ${val}`;
        }
      });

      return cmd;
    }

    // Special handling for wget lambda zip
    if (selectedCommand.label === "wget lambda zip") {
      selectedCommand.options.forEach((opt) => {
        let val = optionValues[opt.name] || opt.placeholder || "";

        if (opt.name === "url") {
          if (val) {
            cmd += ` "${val}"`; // wrap URL in quotes!
          }
        } else if (val) {
          cmd += ` ${val}`;
        }
      });

      return cmd;
    }

    // General handler for other commands
    selectedCommand.options.forEach((opt) => {
      let val = optionValues[opt.name] || opt.placeholder || "";

      if (opt.type === "flag") {
        if (optionValues[opt.name]) {
          cmd += ` --${opt.name}`;
        }
      } else if (opt.type === "positional") {
        if (val) {
          cmd += ` ${val}`;
        }
      } else if (val) {
        cmd += ` --${opt.name} ${val}`;
      }
    });

    return cmd;
  };


  const handleChange = (name, value) => {
    setOptionValues({ ...optionValues, [name]: value });
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(buildCommand());
    setCopyStatus("Copied!");

    setTimeout(() => {
      setCopyStatus("Copy to Clipboard");
    }, 1500);
  };

  return (
    <Page cls='about' title='Chai Cheng Xun | AWS Command Builder' >
      <div className="content builder-container fcc">

        <h2 className="command-builder-title d-title">AWS CLI Command Builder</h2>
        <p className="builder-description d-text">
          This tool is part of the <strong>Attack the Cloud: AWS Security</strong> workshop. It helps beginners understand AWS CLI parameters instead of memorizing full commands. All commands here are used in the workshop.
        </p>


        <div className="command-builder fcc">
          <div className="form-group d-text">
            <label>Command</label>
            <select
              value={selectedCommand.label}
              onChange={(e) => {
                const cmd = COMMANDS.find((c) => c.label === e.target.value);
                setSelectedCommand(cmd);
                setOptionValues({});
              }}
            >
              {COMMANDS.map((cmd) => (
                <option key={cmd.label} value={cmd.label}>
                  {cmd.label}
                </option>
              ))}
            </select>
          </div>

          {selectedCommand.options.length > 0 && (
            <div className="form-options d-text">
              {selectedCommand.options.map((opt) => (
                <div key={opt.name} className="form-group d-text">
                  {opt.type === "flag" ? (
                    <label className="checkbox-label d-text">
                      <input
                        type="checkbox"
                        checked={!!optionValues[opt.name]}
                        onChange={(e) => handleChange(opt.name, e.target.checked)}
                      />
                      {opt.name}
                    </label>
                  ) : (
                    <>
                      <label>{opt.name}</label>
                      <input
                        type="text"
                        placeholder={opt.placeholder}
                        value={optionValues[opt.name] || ""}
                        onChange={(e) => handleChange(opt.name, e.target.value)}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="form-group d-text">
            <label>Built Command</label>
            <textarea
              readOnly
              value={buildCommand()}
              rows="4"
              className="command-output"
            />
          </div>

          <div className="form-group d-text fcs">
            <label className="checkbox-label d-text fcc">
              <input
                type="checkbox"
                checked={useLocalStack}
                onChange={(e) => setUseLocalStack(e.target.checked)}
              />
              Using LocalStack (--endpoint-url=http://localhost:4566)
            </label>
          </div>

          <button onClick={handleCopy} className="copy-button d-button">
            {copyStatus}
          </button>
        </div>
      </div>
    </Page>
  );
}

export default AwsCommandBuilder;
